import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Redirect } from 'react-router';
import get from 'lodash/get';
import classNames from 'classnames';
import isAfter from 'date-fns/is_after';

import { Book } from 'rebook/apps/books/models';
import SpinnerLoader from 'rebook/apps/base/components/SpinnerLoader';
import formatCurrency from 'rebook/apps/utils/text/formatCurrency';
import Form, { FormRow, FormControl } from 'rebook/apps/base/components/form/Form';
import FormTextInput from 'rebook/apps/base/components/form/FormTextInput';
import fetchQuery from 'rebook/apps/utils/fetchQuery';
import Button from 'rebook/apps/base/components/Button';
import ModalDialog from 'rebook/mybook/App/ModalDialogs/ModalDialog';
import validateForm from 'rebook/apps/base/components/form/validateForm';
import pluralize from 'rebook/apps/utils/text/pluralize';
import * as BookDetailActions from 'rebook/apps/books/actions/BookDetailActions';
import * as NotificationActions from 'rebook/apps/users/actions/NotificationActions';
import * as SeoActions from 'rebook/apps/base/actions/SeoActions';
import BookRentHeader from 'rebook/apps/books/components/BookRentHeader';
import BookRentInfo from 'rebook/apps/books/components/BookRentInfo';
import BookRentIncludeSubscription from 'rebook/apps/books/components/BookRentIncludeSubscription';
import BookRentAppButtons from 'rebook/apps/books/components/BookRentAppButtons';
import { BOOK_RENT_ERROR_MESSAGES } from 'rebook/apps/books/constants';

import styles from './style.scss';


class BookBuyRentView extends React.Component {
  static fetchInitialDataForServer(match, store) {
    return store.dispatch(SeoActions.setData({
      robots: 'noindex',
    }));
  }
  constructor(props) {
    super(props);

    this.state = {
      form: {},
      errorList: {},
      isDisabled: false,
      isInProgress: false,
      rentPayment: null,
    };
  }

  componentDidMount() {
    const { match, location } = this.props;
    this.props.BookDetailActions.fetchInitialData(match, location);
  }

  componentWillReceiveProps({ match, location }) {
    const currentBookSlug = this.props.match.params.bookSlug;
    const newBookSlug = match.params.bookSlug;
    if (newBookSlug && newBookSlug !== currentBookSlug) {
      this.props.BookDetailActions.resetData();
      this.props.BookDetailActions.fetchInitialData(match, location);
    }
  }

  componentWillUnmount() {
    this.props.SeoActions.resetData();
  }

  handleCloseModalWindow() {
    this.setState({ rentPayment: null });
  }

  getPayDialog() {
    const { rentPayment } = this.state;
    if (!rentPayment) {
      return null;
    }

    return (
      <ModalDialog onClose={() => { this.handleCloseModalWindow(); }} dialog={
        {
          id: '1',
          extraClassName: 'jest-payonline-window',
          emojiIcon: '',
          title: '',
          content:
            <div>
              <iframe
                width="100%"
                height="516"
                className={styles.trialForm}
                src={rentPayment.redirect_url}
                frameBorder="0" />
            </div>,
        }
      } />
    );
  }

  handleOpenPayonline() {
    const { book } = this.props.bookDetail;
    const rentProductId = get(book, 'rent_product.id', null);

    this.setState({
      errorList: {},
      isInProgress: true,
    });

    const options = {
      method: 'post',
      json: {
        provider: 'web_rent',
        data_rent: {
          payment_method: 'credit_card',
          rent_product_id: rentProductId,
        },
      },
    };

    fetchQuery('/api/payments/', options)
      .then(({ data }) => {
        this.setState({
          rentPayment: data,
          isInProgress: false,
        });
      })
      .catch(({ data, resp }) => {
        Raven.captureMessage('failed to submit rent payment due to server error', {
          level: 'error',
          extra: {
            status: resp.status,
            response: data,
          },
        });
        this.setState({ isInProgress: false });
        this.props.NotificationActions.pop(data[0], 'error');
      });
  }

  onBeforeSubmit() {
    ga('send', 'event', {
      eventCategory: 'Registration',
      eventAction: 'PushButton',
      eventLabel: 'Rent',
    });
  }

  updateFormField(input) {
    this.setState(
      (prevState) => {
        return {
          errorList: {},
          form: {
            ...prevState.form,
            ...input,
          },
        };
      }
    );
  }

  handleSubmit(submit) {
    const { user, bookDetail } = this.props;
    const { book } = bookDetail;
    if (!user) {
      const form = validateForm(this.state.form);

      if (form.errorList) {
        this.handleError(form.errorList);
        return;
      }

      submit(form);
      this.setState({
        errorList: {},
        isInProgress: true,
      });
    }
    else {
      ga('send', 'event', {
        eventCategory: 'Payment',
        eventAction: 'PushPurchase',
        eventLabel: book.name,
      });
      this.handleOpenPayonline();
    }
  }

  handleSuccess() {
    const { book } = this.props.bookDetail;
    const bookUrl = get(book, 'absolute_url', null);
    this.setState({ isInProgress: false });
    ga('send', 'event', {
      eventCategory: 'Registration',
      eventAction: 'Registration',
      eventLabel: 'Rent',
    });
    window.location.assign(`${bookUrl}buy_rent/`);
  }

  handleError(errorList) {
    this.setState({
      errorList,
      isInProgress: false,
    });

    if (errorList.non_field_errors) {
      this.props.NotificationActions.pop(errorList.non_field_errors, 'error');
    }
  }

  render() {
    const { bookDetail, user } = this.props;
    const { rentPayment, errorList, isDisabled, isInProgress } = this.state;
    const { book } = bookDetail;
    const theBook = new Book(book);
    const rentProduct = get(theBook, 'rent_product', null);
    const price = get(theBook, 'rent_product.web_price', null);
    const rentValidTill = get(theBook, 'rent_valid_till', null);
    const isRented = get(theBook, 'is_rented', null) && isAfter(rentValidTill, new Date());
    const daysPeriod = get(theBook, 'rent_product.days', null);
    const subscriptionId = get(theBook, 'subscription_id', '');
    const isAvailableForUser = !!get(theBook, 'available_for_user', false);
    const isAvailable = !!get(theBook, 'available', false);
    const isAudio = get(theBook, 'type', '') === 'audio';
    const modalWindow = this.getPayDialog();

    if (book && (!subscriptionId || !rentProduct || !isAvailable)) {
      return <Redirect to={book.absolute_url} />;
    }

    const formattedPrice = formatCurrency(price, 'RUB');

    return (
      <div className={styles.bookBuyRent}>
        {rentPayment ? modalWindow : null}
        {!book && <SpinnerLoader />}
        {(book && isRented) && (
          <React.Fragment>
            <BookRentHeader book={theBook} />
            <BookRentInfo book={theBook} />
            <BookRentAppButtons isAudio={isAudio} />
          </React.Fragment>
        )}
        {(book && !isRented && isAvailableForUser) && (
          <React.Fragment>
            <BookRentHeader book={theBook} />
            <BookRentIncludeSubscription book={theBook} />
            <BookRentAppButtons isAudio={isAudio} />
          </React.Fragment>
        )}
        {(book && !isRented && !isAvailableForUser) && (
          <React.Fragment>
            <BookRentHeader book={theBook} />
            <div className={classNames(styles.formContainer, isAudio && styles.isAudio)}>
              <div className={styles.formContainerHeader}>
                <span><b>Аренда</b></span>
                <span className={styles.formContainerHeaderPrice}>
                  {formattedPrice}
                </span>
                <span className={styles.formContainerHeaderTitle}>
                  Доступ к этой {isAudio ? 'аудиокниге' : 'книге'} <br />
                  на {daysPeriod} {pluralize(daysPeriod, 'дня', 'дни', 'дней')}
                </span>
              </div>
              <div className={styles.formInputsContainer}>
                <Form
                  action="/api/registration/"
                  method="post"
                  extraClassName={classNames('jest-rent-form', styles.form)}
                  onBeforeSubmit={() => this.onBeforeSubmit()}
                  onSubmit={(callback) => this.handleSubmit(callback)}
                  onSuccess={(data) => this.handleSuccess(data)}
                  onError={(errorList) => this.handleError(errorList)}>
                  {!user && (
                    <FormRow>
                      <FormTextInput
                        extraClassName={classNames('jest-email-form-input', styles.input)}
                        validationMessageExtraClassName="jest-email-form-input-error"
                        errorList={errorList.email}
                        isDisabled={isDisabled || isInProgress}
                        isRequired
                        isRequiredError={BOOK_RENT_ERROR_MESSAGES.EMAIL_IS_REQUIRED}
                        label="Ввести e-mail"
                        onUpdate={(input) => this.updateFormField(input)}
                        name="email" />
                    </FormRow>
                  )}
                  <FormControl>
                    <Button
                      type="submit"
                      extraClassName="jest-registration-form-submit"
                      size="small"
                      isInProgress={isInProgress}>
                      Оформить аренду
                    </Button>
                  </FormControl>
                </Form>
              </div>
            </div>
            <BookRentAppButtons isAudio={isAudio} />
          </React.Fragment>
        )}
      </div>
    );
  }
};

const mapStateToProps = (state) => {
  return {
    user: state.user,
    bookDetail: state.bookDetail,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    BookDetailActions: bindActionCreators(BookDetailActions, dispatch),
    NotificationActions: bindActionCreators(NotificationActions, dispatch),
    SeoActions: bindActionCreators(SeoActions, dispatch),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(BookBuyRentView);
